/**
 * Manages the "Made in USA" image overlay for Rio Gallery (Variant Image Wizard).
 *
 * The Rio gallery is a React app that re-renders its slide DOM on variant change,
 * navigation and lazy-load swaps. A plain injected node + a one-shot slide observer
 * do not survive those re-renders (React discards the foreign node and the observer
 * ends up watching detached slides), which is why the badge vanished after the first
 * interaction and never came back.
 *
 * Fix: glue a single badge into the MAIN slide ([data-index="0"]) and re-assert it via
 * a debounced subtree MutationObserver so every re-render immediately gets it back.
 * The swiper translates the wrapper, so the badge slides off/on with the main image
 * (main-image-only, moves with the animation, never permanently gone).
 */
(function () {
  'use strict';

  let imageUrl = null;
  let marketHandle = null;
  let madeInUsaVariantIds = [];

  let rioGallery = null;
  let variantPicker = null;
  let currentVariantId = null;

  let observer = null;
  let rafPending = false;

  function init() {
    const productMedia = document.querySelector('#product-media');
    if (!productMedia) {
      setTimeout(init, 200);
      return;
    }

    imageUrl = productMedia.dataset.madeInUsaImage;
    marketHandle = productMedia.dataset.marketHandle;
    const variantIdsStr = productMedia.dataset.madeInUsaVariantIds;

    if (variantIdsStr) {
      madeInUsaVariantIds = variantIdsStr.split(',').map((id) => id.toString());
    }

    if (!imageUrl) return; // No badge image configured
    if (marketHandle !== 'us') return; // Only the US market shows the badge
    if (madeInUsaVariantIds.length === 0) return; // No qualifying variants

    waitForGallery();
    setupVariantPicker();
  }

  function waitForGallery() {
    rioGallery = document.querySelector('.rio-media-gallery');
    if (!rioGallery) {
      setTimeout(waitForGallery, 200);
      return;
    }

    ensureBadge();
    setupObserver();
  }

  /**
   * Returns the main-image slide (first image of the current variant's group).
   */
  function getMainSlide() {
    if (!rioGallery) return null;
    return (
      rioGallery.querySelector('.pwzrswiper-slide[data-index="0"]') ||
      rioGallery.querySelector('.pwzrswiper-slide')
    );
  }

  function variantQualifies() {
    if (marketHandle !== 'us') return false;
    if (!currentVariantId) return false;
    return madeInUsaVariantIds.includes(currentVariantId.toString());
  }

  /**
   * Idempotently syncs the badge to the main slide: present + parented correctly when
   * the variant qualifies, removed otherwise. Safe to call on every re-render.
   */
  function ensureBadge() {
    if (!rioGallery) return;

    const shouldShow = variantQualifies();
    const mainSlide = getMainSlide();

    // Remove any stray badges that are no longer on the current main slide.
    rioGallery.querySelectorAll('.made-in-usa-image-overlay').forEach((el) => {
      if (!shouldShow || el.parentElement !== mainSlide) {
        el.remove();
      }
    });

    if (!shouldShow || !mainSlide) return;

    // Give the slide a positioning context for the absolutely-positioned badge.
    if (window.getComputedStyle(mainSlide).position === 'static') {
      mainSlide.style.position = 'relative';
    }

    if (mainSlide.querySelector(':scope > .made-in-usa-image-overlay')) return;

    const overlay = document.createElement('div');
    overlay.className = 'made-in-usa-image-overlay';
    overlay.style.cssText = 'pointer-events: none; display: block; position: absolute;';

    const img = document.createElement('img');
    img.src = imageUrl;
    img.alt = 'Made in USA';
    img.className = 'made-in-usa-image-overlay__img';
    overlay.appendChild(img);

    mainSlide.appendChild(overlay);
  }

  function scheduleEnsure() {
    if (rafPending) return;
    rafPending = true;
    requestAnimationFrame(() => {
      rafPending = false;
      ensureBadge();
    });
  }

  /**
   * Re-asserts the badge after every React re-render / slide rebuild.
   */
  function setupObserver() {
    if (!rioGallery) return;
    if (observer) observer.disconnect();

    observer = new MutationObserver(scheduleEnsure);
    observer.observe(rioGallery, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'src', 'data-index'],
    });
  }

  function setupVariantPicker() {
    variantPicker = document.querySelector('variant-picker');
    if (!variantPicker) {
      setTimeout(setupVariantPicker, 200);
      return;
    }

    // React/theme may populate `.variant` a bit after the element upgrades, and
    // `on:variant:change` only fires on interaction, so poll for the initial variant.
    let attempts = 0;
    (function seedInitialVariant() {
      if (variantPicker.variant && variantPicker.variant.id) {
        currentVariantId = variantPicker.variant.id;
        scheduleEnsure();
      } else if (attempts < 50) {
        attempts += 1;
        setTimeout(seedInitialVariant, 200);
      }
    })();

    variantPicker.addEventListener('on:variant:change', (evt) => {
      const variant = evt.detail && evt.detail.variant;
      currentVariantId = variant && variant.id ? variant.id : null;
      scheduleEnsure();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    setTimeout(init, 100);
  }
})();
